/* ============================================================
   Journey Skincare — client-side cart (prototype)
   Persists to localStorage. Powers the drawer + checkout.
   ============================================================ */

const FALLBACK_CATALOG = {
  sunscreen: { name: "Invisible Sunscreen SPF 50", price: 1079, img: "https://thejourneyskincare.com/cdn/shop/files/Sunscreen_website_main.jpg?v=1771357612&width=200" },
  serum:     { name: "Vitamin C 20%",              price: 809,  img: "https://thejourneyskincare.com/cdn/shop/files/Serum_website_main.jpg?v=1771357629&width=200" },
  bundle:    { name: "Pigmentation Defense Duo",     price: 1799, img: "https://thejourneyskincare.com/cdn/shop/t/17/assets/routine-duo.jpg" },
  "test-sunscreen": { name: "test sunscreen",     price: 1,    img: "https://thejourneyskincare.com/cdn/shop/files/Sunscreen_website_main.jpg?v=1771357612&width=200" }
};
const CATALOG = Object.assign({}, FALLBACK_CATALOG, window.CATALOG || {});
const FREE_SHIP_THRESHOLD = 999;
const SHIP_FEE = 49;
const CART_KEY = "journeyCart";

function getCart() { try { return JSON.parse(localStorage.getItem(CART_KEY)) || {}; } catch (e) { return {}; } }
function saveCart(c) {
  localStorage.setItem(CART_KEY, JSON.stringify(c));
  clearAppliedCoupon(); // cart contents changed — any applied coupon must be re-verified against the new cart
  renderCart();
  syncShopifyCart().catch(err => console.error("Real-time cart sync failed:", err));
}
function clearCart() {
  localStorage.removeItem(CART_KEY);
  clearAppliedCoupon();
  renderCart();
  // Route the clear through the same serialized sync so it can't race a pending update.
  syncShopifyCart().catch(err => console.error("Real-time cart clear failed:", err));
}

function cartCount(c) { c = c || getCart(); return Object.values(c).reduce((a, b) => a + b, 0); }
function subtotal(c) { c = c || getCart(); return Object.entries(c).reduce((s, [id, q]) => s + (CATALOG[id] ? CATALOG[id].price : 0) * q, 0); }
function shipping(sub) { return sub === 0 || sub >= FREE_SHIP_THRESHOLD ? 0 : SHIP_FEE; }
function fmt(n) { return "₹" + n.toLocaleString("en-IN"); }

function addToCart(id, qty) {
  if (!CATALOG[id]) return;
  qty = qty || 1;
  const c = getCart();
  c[id] = (c[id] || 0) + qty;
  saveCart(c);
  openCart();
}
function changeQty(id, d) {
  const c = getCart();
  c[id] = (c[id] || 0) + d;
  if (c[id] <= 0) delete c[id];
  saveCart(c);
}
function removeItem(id) { const c = getCart(); delete c[id]; saveCart(c); }
function getPdpQty() { const el = document.getElementById("qty"); return el ? parseInt(el.textContent, 10) || 1 : 1; }

const FALLBACK_VARIANT_MAPPING = {
  sunscreen: 50832024142114,
  serum: 50811644805410,
  bundle: 52608427065634,
  "test-sunscreen": 52007206551842
};
const VARIANT_MAPPING = Object.assign({}, FALLBACK_VARIANT_MAPPING, window.VARIANT_MAPPING || {});

// Serialize syncs so overlapping cart edits can never leave Shopify's cart
// momentarily empty (which made Magic Checkout bounce to the home page).
let _syncChain = Promise.resolve();
function syncShopifyCart() {
  const btns = document.querySelectorAll('button[name="checkout"]');
  btns.forEach(b => { b.disabled = true; b.style.opacity = '0.7'; b.textContent = 'Syncing...'; });
  _syncChain = _syncChain.then(_doSyncShopifyCart, _doSyncShopifyCart).finally(() => {
    btns.forEach(b => { b.disabled = false; b.style.opacity = '1'; b.textContent = 'Checkout'; });
  });
  return _syncChain;
}

async function _doSyncShopifyCart() {
  const c = getCart();

  // Desired quantities keyed by Shopify variant id. "Routine" is its own real
  // product/variant (SKU JS-TTB-ROUTINE) — it is not expanded into its
  // component variants.
  const desired = {};
  const want = (variantId, qty) => {
    if (!variantId || qty <= 0) return;
    desired[variantId] = (desired[variantId] || 0) + qty;
  };
  if (c.sunscreen) want(VARIANT_MAPPING.sunscreen, c.sunscreen);
  if (c.serum) want(VARIANT_MAPPING.serum, c.serum);
  if (c["test-sunscreen"]) want(VARIANT_MAPPING["test-sunscreen"], c["test-sunscreen"]);
  if (c.bundle) want(VARIANT_MAPPING.bundle, c.bundle);

  // Read Shopify's current cart so we can reconcile toward `desired` WITHOUT ever
  // clearing (which would leave a transient-empty window that made Magic Checkout
  // bounce to the home page). NOTE: /cart/update.js can only change quantities of
  // items already in the cart — it CANNOT add a new variant (verified live: it
  // returns 422). So new items / increases must go through /cart/add.js.
  const current = {};
  try {
    const cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json());
    (cart.items || []).forEach(item => { current[item.id] = (current[item.id] || 0) + item.quantity; });
  } catch (e) {
    // If we can't read the cart, treat it as empty; add.js below will populate it.
  }

  // 1) New items and quantity increases -> /cart/add.js (adds the delta on top).
  const addItems = [];
  Object.keys(desired).forEach(id => {
    const delta = desired[id] - (current[id] || 0);
    if (delta > 0) addItems.push({ id: Number(id), quantity: delta });
  });
  if (addItems.length) {
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: addItems })
    });
  }

  // 2) Decreases and removals -> /cart/update.js with absolute quantities (0 removes).
  //    Only touches line items already present, so there's no empty-cart window.
  const updates = {};
  Object.keys(current).forEach(id => {
    const wanted = desired[id] || 0;
    if (wanted < current[id]) updates[id] = wanted;
  });
  if (Object.keys(updates).length) {
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates })
    });
  }
}

// ---- Coupon codes (validated against Shopify's own cart, not client math) ----
// Applying a coupon syncs our local cart into Shopify's real cart, applies the
// code via Shopify's own /discount/:code endpoint, then reads back the
// discount Shopify itself computed. That cart token is passed to the backend
// at checkout so it can independently re-verify the same discount — we never
// tell the backend "trust me, it's ₹X off."
let _appliedCoupon = null; // { code, discount, cartToken }

function clearAppliedCoupon() {
  _appliedCoupon = null;
}

function applyCoupon(btn) {
  const wrap = btn.closest('.coupon-box');
  const input = wrap.querySelector('.coupon-input');
  const errorEl = wrap.querySelector('.coupon-error');
  const code = (input.value || '').trim();
  errorEl.style.display = 'none';
  if (!code) return;

  btn.disabled = true; btn.textContent = 'Applying...';

  syncShopifyCart()
    .then(() => fetch('/discount/' + encodeURIComponent(code)))
    .then(() => fetch('/cart.js', { headers: { 'Accept': 'application/json' } }))
    .then(r => r.json())
    .then(cartData => {
      const applied = (cartData.discount_codes || []).find(
        d => d.applicable && d.code.toLowerCase() === code.toLowerCase()
      );
      if (!applied) {
        throw new Error('Invalid or expired code');
      }
      _appliedCoupon = {
        code: applied.code,
        discount: (cartData.total_discount || 0) / 100,
        cartToken: cartData.token
      };
      renderCart();
    })
    .catch(err => {
      errorEl.textContent = err.message || 'Could not apply that code.';
      errorEl.style.display = 'block';
    })
    .finally(() => {
      btn.disabled = false; btn.textContent = 'Apply';
    });
}

function removeCoupon() {
  clearAppliedCoupon();
  renderCart();
}



function buyNow(id, qty) {
  addToCart(id, qty);
}


function openCart() {
  const d = document.querySelector(".cart-drawer"), b = document.querySelector(".cart-backdrop");
  if (d) d.classList.add("open");
  if (b) b.classList.add("open");
  document.body.classList.add("menu-open");
}
function closeCart() {
  const d = document.querySelector(".cart-drawer"), b = document.querySelector(".cart-backdrop");
  if (d) d.classList.remove("open");
  if (b) b.classList.remove("open");
  document.body.classList.remove("menu-open");
}

function renderCart() {
  const c = getCart();
  const ids = Object.keys(c);
  const sub = subtotal(c);
  const ship = shipping(sub);

  // header counts
  document.querySelectorAll(".cart-count").forEach(e => e.textContent = cartCount(c));

  // drawer body
  const body = document.querySelector(".cart-body");
  const foot = document.querySelector(".cart-foot");
  if (body) {
    if (!ids.length) {
      body.innerHTML = '<div class="cart-empty">Your cart is empty.<br>Two products, one honest routine.<a href="/#bundle" class="btn btn-block" onclick="closeCart()">Shop now</a></div>';
      if (foot) foot.style.display = "none";
    } else {
      body.innerHTML = ids.map(id => {
        const p = CATALOG[id], q = c[id];
        return '<div class="cart-item">' +
          '<div class="ci-media" style="background-image:url(\'' + p.img + '\')"></div>' +
          '<div class="ci-body"><h4>' + p.name + '</h4>' +
          '<div class="ci-controls"><div class="ci-stepper">' +
          '<button onclick="changeQty(\'' + id + '\',-1)">−</button><span class="n">' + q + '</span>' +
          '<button onclick="changeQty(\'' + id + '\',1)">+</button></div>' +
          '<button class="ci-remove" onclick="removeItem(\'' + id + '\')">Remove</button></div></div>' +
          '<div class="ci-line">' + fmt(p.price * q) + '</div></div>';
      }).join("");
      if (foot) {
        foot.style.display = "block";
        const note = foot.querySelector(".ship-note");
        if (sub >= FREE_SHIP_THRESHOLD) { note.textContent = "✓ You've unlocked free shipping"; note.classList.remove("warn"); }
        else { note.textContent = "Add " + fmt(FREE_SHIP_THRESHOLD - sub) + " more for free shipping"; note.classList.add("warn"); }
        renderCouponBox(foot);
        const couponDiscount = _appliedCoupon ? _appliedCoupon.discount : 0;
        const total = Math.max(0, sub - couponDiscount + ship);
        foot.querySelector(".cart-subtotal").textContent = fmt(sub);
        foot.querySelector(".cart-shipping").textContent = ship === 0 ? "FREE" : fmt(ship);
        foot.querySelector(".cart-total").textContent = fmt(total);
      }
    }
  }

}

function renderCouponBox(foot) {
  let box = foot.querySelector(".coupon-box");
  if (!box) {
    box = document.createElement("div");
    box.className = "coupon-box";
    const note = foot.querySelector(".ship-note");
    note.insertAdjacentElement("afterend", box);
  }
  if (_appliedCoupon) {
    box.innerHTML =
      '<div class="coupon-applied">' +
        '<span>✓ "' + _appliedCoupon.code + '" applied — ' + fmt(_appliedCoupon.discount) + ' off</span>' +
        '<button type="button" onclick="removeCoupon()">Remove</button>' +
      '</div>';
  } else {
    box.innerHTML =
      '<div class="coupon-row">' +
        '<input type="text" class="coupon-input" placeholder="Coupon code">' +
        '<button type="button" onclick="applyCoupon(this)">Apply</button>' +
      '</div>' +
      '<p class="coupon-error" style="display:none;"></p>';
  }

  let discountRow = foot.querySelector(".row.discount");
  if (_appliedCoupon && _appliedCoupon.discount > 0) {
    if (!discountRow) {
      discountRow = document.createElement("div");
      discountRow.className = "row discount";
      foot.querySelector(".cart-subtotal").closest(".row").insertAdjacentElement("afterend", discountRow);
    }
    discountRow.innerHTML = '<span>Discount</span><span>-' + fmt(_appliedCoupon.discount) + '</span>';
  } else if (discountRow) {
    discountRow.remove();
  }
}


document.addEventListener("DOMContentLoaded", () => {
  renderCart();
  syncShopifyCart().catch(err => console.error("Initial cart sync failed:", err));
});









// Checkout hands off to Razorpay Magic Checkout. We first reconcile Shopify's
// real cart with our local cart (syncShopifyCart never clears, so there is no
// transient-empty window), then click the native name="checkout" button that
// magic-shopify.js binds to. Magic Checkout opens its own modal and collects
// the address + payment, so no custom backend, order creation, or address form
// is needed here anymore.
async function checkoutNative(btn) {
  const cart = getCart();
  if (!Object.keys(cart).length) { alert('Your cart is empty.'); return; }
  const originalText = btn ? btn.textContent : null;
  if (btn) { btn.disabled = true; btn.textContent = 'Loading...'; }

  try {
    // Make sure Shopify's cart matches what the shopper sees locally.
    await syncShopifyCart();

    const rzpBtn = document.getElementById('rzp-native-checkout-btn');
    if (rzpBtn) {
      // A real click is what magic-shopify.js listens for; it intercepts this
      // and opens Magic Checkout. If Magic is not active on the store, the
      // button submits the native cart form to Shopify's checkout instead.
      rzpBtn.click();
    } else {
      window.location.href = '/checkout';
    }
  } catch (err) {
    console.error('Checkout failed:', err);
    alert('Could not start checkout. Please try again.');
  } finally {
    // Restore the visible button (Magic opens in an overlay; the shopper may
    // close it and click again).
    if (btn) { btn.disabled = false; if (originalText) btn.textContent = originalText; }
  }
}
